import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { displayUserPhone } from "../../utils/staffPhone";

export default function SpinManagement() {
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_BACKEND_LOCAL_API
      : process.env.REACT_APP_BACKEND_LIVE_API;

  const [spinWheelEnabled, setSpinWheelEnabled] = useState(true);
  const [spinMinWeeklyDeposit, setSpinMinWeeklyDeposit] = useState(30);
  const [spinGamesPerCredit, setSpinGamesPerCredit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const readSpinOnFromSettings = (data) => {
    if (!data || data.spinWheelEnabled == null) return true;
    const v = data.spinWheelEnabled;
    if (v === false || v === "false" || v === 0 || v === "0") return false;
    return true;
  };

  const loadSettings = useCallback(async () => {
    const res = await axios.get(baseUrl + "settings/data").catch(() => null);
    if (res?.data) {
      setSpinWheelEnabled(readSpinOnFromSettings(res.data));
      setSpinMinWeeklyDeposit(Number(res.data.spinMinWeeklyDeposit) || 30);
      setSpinGamesPerCredit(Number(res.data.spinGamesPerCredit) || 10);
    }
  }, [baseUrl]);

  const loadHistory = useCallback(async () => {
    const res = await axios.get(
      baseUrl + `admin/spin/history?page=${page}&_limit=25`,
      { headers: headers() }
    );
    setHistory(res.data.rows || []);
    setTotalPages(res.data.totalPages || 1);
  }, [baseUrl, page]);

  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      try {
        await loadSettings();
        if (ok) await loadHistory();
      } catch (e) {
        console.error(e);
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [loadSettings, loadHistory]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(
        baseUrl + "admin/spin/settings",
        {
          spinWheelEnabled: Boolean(spinWheelEnabled),
          spinMinWeeklyDeposit: Number(spinMinWeeklyDeposit) || 30,
          spinGamesPerCredit: Number(spinGamesPerCredit) || 10,
        },
        { headers: { ...headers(), "Content-Type": "application/json" } }
      );
      await loadSettings();
      alert("Spin settings saved.");
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.msg ||
        (typeof e.response?.data === "string" ? e.response.data : null) ||
        e.message ||
        "Save failed.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-content p-4">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="admin-content p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Spin wheel</h4>
        <span className="text-muted small">
          Weekly deposit → 24h free spins + game credits (see below)
        </span>
      </div>

      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <div className="form-group d-flex align-items-center flex-wrap">
            <label className="mb-0 mr-3 font-weight-bold">Spin ON / OFF</label>
            <button
              type="button"
              className={`btn btn-sm ${spinWheelEnabled ? "btn-success" : "btn-secondary"}`}
              onClick={() => setSpinWheelEnabled(!spinWheelEnabled)}
            >
              {spinWheelEnabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Minimum weekly deposit (₹)</label>
              <input
                type="number"
                min={1}
                className="form-control"
                value={spinMinWeeklyDeposit}
                onChange={(e) => setSpinMinWeeklyDeposit(e.target.value)}
              />
              <small className="text-muted">
                One such deposit this week unlocks: free spin every 24h until week end (UTC Mon–Sun), plus game-based credits.
              </small>
            </div>
            <div className="form-group col-md-4">
              <label>Completed games per 1 spin credit</label>
              <input
                type="number"
                min={1}
                className="form-control"
                value={spinGamesPerCredit}
                onChange={(e) => setSpinGamesPerCredit(e.target.value)}
              />
              <small className="text-muted">
                Each player gets progress when a challenge completes.
              </small>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={saveSettings}
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Spin history (last 24 hours)</span>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="small text-muted mr-2">
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No spins in the last 24 hours.
                  </td>
                </tr>
              )}
              {history.map((row, i) => (
                <tr key={row._id}>
                  <td>{(page - 1) * 25 + i + 1}</td>
                  <td>{row.user?.Name || "—"}</td>
                  <td>{displayUserPhone(row.user?.Phone)}</td>
                  <td>₹{row.amountWon ?? 0}</td>
                  <td>
                    {row.spinTime
                      ? new Date(row.spinTime).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    {row.user?._id && (
                      <Link
                        className="btn btn-sm btn-outline-info"
                        to={`/user/view_user/${row.user._id}`}
                      >
                        Profile
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
